/**
 * Шапка страницы агроцикла и ряд фактов объёма (agroai-0e8, замечания
 * владельца 08.10).
 *
 * Шапка: название цикла и статус — одной строкой, под ними культура, сезон,
 * окно и дата среза; справа ОДНА группа — выбор цикла и действие менеджера
 * (шаблон, «Начать агроцикл», «Править план»).
 *
 * Факты: земля, хозяйства, поля, работы плана (запущено K из M · идёт N ·
 * впереди), текущий этап. Оценочных долей и просрочки здесь нет — они ниже,
 * по этапам и работам, со своей основой. Всё — из ОДНОГО ответа
 * `/agrocycles/{key}`: шапка не ждёт картины сезона.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Building2, CalendarClock, LandPlot, ListChecks, MapPin, Pencil, ScrollText } from 'lucide-react';
import { Badge } from '../../ui/Badge';
import { Grid } from '../../ui/Card';
import { KpiCard } from '../../ui/KpiCard';
import { Skeleton } from '../../ui/Skeleton';
import { defineMessages } from '../../../i18n/define';
import { UNIT_LABELS, useT } from '../../../i18n/hooks';
import { dateShort, windowLabel } from '../../lib/format';
import { cropName } from '../../../lib/crops';
import type { AgrocycleDetail } from '../agrocycle/agrocycleApi';
import { kpi, num, statusLabel, windowText } from '../agrocycle/agrocycleView';
import { CyclePicker, type CycleOption } from './CyclePicker';
import { planFacts } from './cycleFacts';
import './cyclePage.css';

const m = defineMessages({
  level: { id: 'cycle.head.level', defaultMessage: 'Агроцикл', description: 'Надзаголовок страницы агроцикла (капсом): уровень просмотра' },
  subtitle: {
    id: 'cycle.head.subtitle',
    defaultMessage: '{crop} · сезон {season} · {window}',
    description: 'Строка под названием агроцикла: культура, сезон и окно цикла датами («1 ноя 2025 — 15 дек 2026»)',
  },
  asOf: { id: 'cycle.head.asOf', defaultMessage: 'срез {date}', description: 'Хвост строки под названием агроцикла: дата среза данных; {date} — дата' },
  noWindow: { id: 'cycle.head.noWindow', defaultMessage: 'сроков нет', description: 'Строка под названием агроцикла: у цикла нет сроков' },
  template: { id: 'cycle.head.template', defaultMessage: 'Шаблон', description: 'Кнопка-ссылка в группе действий шапки агроцикла: открыть шаблон культуры' },
  edit: { id: 'cycle.head.edit', defaultMessage: 'Править план', description: 'Кнопка менеджера в шапке агроцикла: открыть план по этапам для правки' },
  actionsAria: { id: 'cycle.head.actionsAria', defaultMessage: 'Агроцикл: выбор и действия', description: 'aria-label группы справа в шапке агроцикла: выбор цикла и действия менеджера' },
  land: { id: 'cycle.head.fact.land', defaultMessage: 'Земля агроцикла', description: 'Подпись факта: уникальная земля агроцикла по размещению, га' },
  landNote: { id: 'cycle.head.fact.landNote', defaultMessage: 'по размещению сезона, каждый гектар один раз', description: 'Подпись под фактом «Земля агроцикла»' },
  farms: { id: 'cycle.head.fact.farms', defaultMessage: 'Хозяйства', description: 'Подпись факта: число хозяйств-участников агроцикла' },
  farmsNote: { id: 'cycle.head.fact.farmsNote', defaultMessage: 'участники по размещению', description: 'Подпись под фактом «Хозяйства»' },
  fields: { id: 'cycle.head.fact.fields', defaultMessage: 'Поля', description: 'Подпись факта: число полей агроцикла (контур или его часть по размещению)' },
  fieldsNote: { id: 'cycle.head.fact.fieldsNote', defaultMessage: 'контур или его часть в цикле', description: 'Подпись под фактом «Поля»' },
  works: { id: 'cycle.head.fact.works', defaultMessage: 'Работы плана', description: 'Подпись факта: работы плана агроцикла — запущено K из M' },
  worksValue: { id: 'cycle.head.fact.worksValue', defaultMessage: '{launched} из {total}', description: 'Значение факта «Работы плана»: запущено K из M; числа строкой' },
  worksNote: {
    id: 'cycle.head.fact.worksNote',
    defaultMessage: 'запущено · идёт {active} · впереди {ahead}',
    description: 'Подпись под фактом «Работы плана»: «запущено» относится к числу над ней; идёт — окно накрывает дату среза; впереди — без кампании; числа строкой',
  },
  stage: { id: 'cycle.head.fact.stage', defaultMessage: 'Текущий этап', description: 'Подпись факта: этап агроцикла, идущий на дату среза' },
  stageNone: { id: 'cycle.head.fact.stageNone', defaultMessage: 'нет', description: 'Значение факта «Текущий этап», когда этапа нет' },
  stageNoneNote: {
    id: 'cycle.head.fact.stageNoneNote',
    defaultMessage: '{status, select, finished {агроцикл завершён} not_started {агроцикл не начат} other {между этапами}}',
    description: 'Подпись под фактом «Текущий этап», когда этапа нет: почему',
  },
});

export interface CycleHeadProps {
  id: string;
  /** Строка цикла сервера; `null` — ещё читается или фикстуры. */
  detail: AgrocycleDetail | null;
  /** Запасные поля шапки на фикстурах. */
  fallback?: { title: string; crop: string; season: string; from: string; to: string; status: AgrocycleDetail['status']; templateId: string } | null;
  title: string;
  asOf: string | null;
  options: CycleOption[];
  /** Кнопка «Начать агроцикл» (менеджер, цикл не начат). */
  start?: ReactNode;
  /** Менеджер правит план начатого цикла. */
  onEdit?: (() => void) | null;
}

export function CycleHead({ id, detail, fallback, title, asOf, options, start, onEdit }: CycleHeadProps) {
  const t = useT();
  const status = detail?.status ?? fallback?.status ?? null;
  const from = detail?.plan_from ?? fallback?.from ?? null;
  const to = detail?.plan_to ?? fallback?.to ?? null;
  const crop = detail ? cropName(detail.crop_code, detail.crop_name) : fallback?.crop ?? null;
  const season = detail?.season_label ?? fallback?.season ?? null;
  const templateId = detail ? String(detail.template_id) : fallback?.templateId ?? null;
  const tone = status === 'started' ? 'green' : status === 'finished' ? 'outline' : 'neutral';
  const window = from && to ? windowLabel(from, to) : t(m.noWindow);
  return (
    <header className="pagehead cyhead" data-guide="cycle-head">
      <div className="pagehead__main">
        <div className="pagehead__level">{t(m.level)}</div>
        <div className="cyhead__title">
          <h1 className="pagehead__title">{title}</h1>
          {status ? <Badge tone={tone}>{statusLabel(status)}</Badge> : null}
        </div>
        {crop && season ? (
          <p className="pagehead__purpose">
            {t(m.subtitle, { crop, season, window })}
            {asOf ? <span className="cyhead__asof"> · {t(m.asOf, { date: dateShort(asOf) })}</span> : null}
          </p>
        ) : (
          <Skeleton width="min(420px, 100%)" />
        )}
      </div>
      <div className="pagehead__actions cyhead__actions" role="group" aria-label={t(m.actionsAria)}>
        <CyclePicker value={id} options={options} />
        <div className="cyhead__buttons">
          {templateId ? (
            <Link className="astra-button" to={`/ops/templates?template=${templateId}`}>
              <ScrollText size={16} aria-hidden="true" /> {t(m.template)}
            </Link>
          ) : null}
          {start ?? null}
          {onEdit ? (
            <button type="button" className="astra-button astra-button-primary" onClick={onEdit}>
              <Pencil size={16} aria-hidden="true" /> {t(m.edit)}
            </button>
          ) : null}
        </div>
      </div>
    </header>
  );
}

/** Ряд фактов объёма цикла — из строки цикла сервера. */
export function CycleFactsRow({ detail, asOf }: { detail: AgrocycleDetail; asOf: string | null }) {
  const t = useT();
  const k = detail.kpis;
  const facts = planFacts(detail, asOf);
  const stage = detail.current_stage;
  const stageWindow = stage
    ? detail.plan.stages.find((s) => s.code === stage.code)
    : null;
  return (
    <div data-guide="cycle-facts">
      <Grid cols={4} className="cyc-facts">
        <KpiCard label={t(m.land)} value={num(kpi(k, 'cycle.land_ha'))} unit={t(UNIT_LABELS.ha)} icon={LandPlot} note={t(m.landNote)} />
        <KpiCard label={t(m.farms)} value={num(kpi(k, 'cycle.farms'))} icon={Building2} note={t(m.farmsNote)} />
        <KpiCard label={t(m.fields)} value={num(kpi(k, 'cycle.fields'))} icon={MapPin} note={t(m.fieldsNote)} />
        <KpiCard
          label={t(m.works)}
          value={facts.total === null ? '—' : t(m.worksValue, { launched: num(facts.launched), total: num(facts.total) })}
          icon={ListChecks}
          note={t(m.worksNote, { active: num(facts.active), ahead: num(facts.ahead) })}
        />
        <KpiCard
          label={t(m.stage)}
          value={stage ? stage.name : t(m.stageNone)}
          icon={CalendarClock}
          note={stage
            ? stageWindow ? windowText({ window_from: stageWindow.window_from, window_to: stageWindow.window_to, approximate: false }) : undefined
            : t(m.stageNoneNote, { status: detail.status })}
        />
      </Grid>
    </div>
  );
}
